import type { AppTab } from './state/appUiStore'

export type AccountActionDetail =
   | {
        action:
           | 'create-sync-key'
           | 'create-qr-code'
           | 'download-data'
           | 'delete-account'
           | 'stop-sync'
     }
   | {
        action: 'enter-sync-key'
        syncKey: string
     }
   | {
        action: 'save-profile'
        heightInput: string
        targetWeightInput: string
        targetRunEffortInput: string
     }

export type AppActionDetail =
   | {
        action: 'retry-sync-now'
     }
   | {
        action: 'select-locale'
        locale: string
     }
   | {
        action: 'select-unit-system'
        unitSystem: 'metric' | 'imperial'
     }
   | {
        action: 'select-history-period'
        periodKey: string
     }
   | {
        action: 'freeze-history-summary'
     }

export type CurrentFastStateDetail = {
   action:
      | 'start-fast'
      | 'end-fast'
      | 'adjust-duration'
      | 'toggle-display-mode'
      | 'edit-fast'
      | 'delete-fast'
   currentFast: {
      startTime: number
      duration: number
      durationUpdatedAt: number
      notifiedCompletion: boolean
      displayMode: 'elapsed' | 'remaining'
   } | null
   fastHistory: Array<{
      startTime: number
      endTime: number
      intendedDuration: number
      historyId?: number | null
   }>
   displayMode: 'elapsed' | 'remaining'
   historyChange?:
      | {
           type: 'edit'
           entry: {
              startTime: number
              endTime: number
              intendedDuration: number
              historyId?: number | null
           }
        }
      | {
           type: 'delete'
           historyId?: number | null
        }
}

type HistoryMonthsDetail = {
   months: number
}

export type WeightStateDetail = {
   action:
      | 'add-weight'
      | 'update-weight'
      | 'delete-weight'
      | 'set-weight-filter'
      | 'toggle-weight-target'
      | 'update-weight-chart-mode'
   weightHistory: Array<{
      weight: number
      timestamp: number
   }>
   weightFilter: 'week' | 'month' | 'quarter' | 'year' | 'max'
   showWeightTarget: boolean
   chartMode: 'line' | 'scatter'
}

export type HabitStateDetail = {
   action:
      | 'add-habit'
      | 'update-habit'
      | 'delete-habit'
      | 'complete-habit'
      | 'toggle-habit-paused'
      | 'set-tag-paused'
      | 'sync-habits'
   habits: Array<{
      id: string
      name: string
      kind: 'recurring' | 'one-off' | 'calendar'
      scheduleDays: number
      dueDate: string | null
      scheduleRule: string
      nextDueAt?: string | null
      paused: boolean
      lastCompletedAt: string | null
      completionHistory: string[]
      createdAt: string
      updatedAt: string
   }>
}

export type LegacyRuntimeHandlers = {
   handleAccountAction: (detail: AccountActionDetail) => void
   handleAppAction: (detail: AppActionDetail) => void
   handleActiveTabChange: (detail: { tab?: AppTab }) => void
   handleCurrentFastStateChange: (detail: CurrentFastStateDetail) => void
   handleHistoryMonthsChange: (detail: HistoryMonthsDetail) => void
   handleHabitStateChange: (detail: HabitStateDetail) => void
   handleWeightStateChange: (detail: WeightStateDetail) => void
   handleMeasurementsStateChange: () => void
   handleFocus: () => void
   handleWideScreenViewportChange: (matches: boolean) => void
}

let legacyRuntimeHandlers: LegacyRuntimeHandlers | null = null
const dispatchesInFlight = new Set<keyof LegacyRuntimeHandlers>()

/**
 * Invokes a legacy runtime handler, guarding against re-entrant dispatches of the same handler.
 *
 * @param handlerKey - The handler to invoke.
 * @param callback - Receives the registered handlers and calls the specific one.
 */
function dispatchToLegacyRuntime(
   handlerKey: keyof LegacyRuntimeHandlers,
   callback: (handlers: LegacyRuntimeHandlers) => void,
) {
   if (!legacyRuntimeHandlers || dispatchesInFlight.has(handlerKey)) {
      return
   }

   dispatchesInFlight.add(handlerKey)
   try {
      callback(legacyRuntimeHandlers)
   } finally {
      dispatchesInFlight.delete(handlerKey)
   }
}

/**
 * Registers the legacy runtime handlers that Solid stores dispatch into.
 *
 * @param handlers - The handler implementations.
 */
export function registerLegacyRuntimeHandlers(handlers: LegacyRuntimeHandlers) {
   legacyRuntimeHandlers = handlers
}

/**
 * Clears the registered legacy runtime handlers.
 */
export function unregisterLegacyRuntimeHandlers() {
   legacyRuntimeHandlers = null
}

/**
 * Dispatches an account action to the legacy runtime.
 *
 * @param detail - The account action detail.
 */
export function dispatchAccountAction(detail: AccountActionDetail) {
   dispatchToLegacyRuntime('handleAccountAction', handlers =>
      handlers.handleAccountAction(detail),
   )
}

/**
 * Dispatches an app-level action to the legacy runtime.
 *
 * @param detail - The app action detail.
 */
export function dispatchAppAction(detail: AppActionDetail) {
   dispatchToLegacyRuntime('handleAppAction', handlers =>
      handlers.handleAppAction(detail),
   )
}

/**
 * Notifies the legacy runtime that the active tab changed.
 *
 * @param tab - The newly active tab.
 */
export function dispatchActiveTabChange(tab: AppTab) {
   dispatchToLegacyRuntime('handleActiveTabChange', handlers =>
      handlers.handleActiveTabChange({ tab }),
   )
}

/**
 * Notifies the legacy runtime of a current-fast state change.
 *
 * @param detail - The current-fast state detail.
 */
export function dispatchCurrentFastStateChange(detail: CurrentFastStateDetail) {
   dispatchToLegacyRuntime('handleCurrentFastStateChange', handlers =>
      handlers.handleCurrentFastStateChange(detail),
   )
}

/**
 * Notifies the legacy runtime that the requested history month count changed.
 *
 * @param months - The number of months to display.
 */
export function dispatchHistoryMonthsChange(months: number) {
   dispatchToLegacyRuntime('handleHistoryMonthsChange', handlers =>
      handlers.handleHistoryMonthsChange({ months }),
   )
}

/**
 * Notifies the legacy runtime of a habit state change.
 *
 * @param detail - The habit state detail.
 */
export function dispatchHabitStateChange(detail: HabitStateDetail) {
   dispatchToLegacyRuntime('handleHabitStateChange', handlers =>
      handlers.handleHabitStateChange(detail),
   )
}

/**
 * Notifies the legacy runtime of a weight state change.
 *
 * @param detail - The weight state detail.
 */
export function dispatchWeightStateChange(detail: WeightStateDetail) {
   dispatchToLegacyRuntime('handleWeightStateChange', handlers =>
      handlers.handleWeightStateChange(detail),
   )
}

/**
 * Notifies the legacy runtime that local measurements changed and need pushing.
 */
export function dispatchMeasurementsStateChange() {
   dispatchToLegacyRuntime('handleMeasurementsStateChange', handlers =>
      handlers.handleMeasurementsStateChange(),
   )
}

/**
 * Notifies the legacy runtime that the window regained focus.
 */
export function dispatchWindowFocus() {
   dispatchToLegacyRuntime('handleFocus', handlers => handlers.handleFocus())
}

/**
 * Notifies the legacy runtime of a wide-screen media-query change.
 *
 * @param matches - Whether the wide-screen breakpoint matches.
 */
export function dispatchWideScreenViewportChange(matches: boolean) {
   dispatchToLegacyRuntime('handleWideScreenViewportChange', handlers =>
      handlers.handleWideScreenViewportChange(matches),
   )
}
